import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  CircularProgress,
  Alert,
  Autocomplete,
  TextField,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FetchData from '../../ServerCall/FetchData';
import {
  callQueryAPI,
  updateUserSelection,
  updateUserSelectionColumn,
} from '../../../redux/ChartPoperties/ChartControlsActions';
import { useDispatch } from 'react-redux';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import { fontSize } from '../../..';
import { useSearchParams } from 'react-router-dom';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

interface ImpersonationProps {
  propKey: string;
  chartControls: any;
}

const Impersonation = ({ propKey, chartControls }: ImpersonationProps) => {
  const currentOperator = chartControls?.properties[propKey]?.rowSecurity?.currentOperator;

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [emailList, setEmailList] = useState<string[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<string | null>(
    chartControls?.properties[propKey]?.rowSecurity?.filterPanels[currentOperator.toLowerCase()]
      ?.filters[0]?.userSelection[0] || '',
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState<boolean>(false);

  const [searchParams] = useSearchParams();
  const workspaceId = searchParams.get('workspaceId');
  const playbookId = searchParams.get('playbookId');

  const showRowSecurity = Object.values(
    chartControls.properties?.[propKey]?.rowSecurity?.filterPanels || {},
  ).some((panel: any) => Array.isArray(panel.filters) && panel.filters.length > 0);

  const showColumnSecurity = Object.values(
    chartControls.properties?.[propKey]?.columnSecurity?.filterPanels || {},
  ).some((panel: any) => Array.isArray(panel.filters) && panel.filters.length > 0);

  const dispatch = useDispatch();

  const fetchUsers = async () => {
    setIsLoading(true);
    setError(null);

    let url = `level-security/users?workspaceId=${workspaceId}`;
    if (playbookId) {
      url = `level-security/users?workspaceId=${workspaceId}&playbookId=${playbookId}`;
    }

    try {
      const response = await FetchData({
        requestType: 'noData',
        method: 'GET',
        url: url,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      if (response.status) {
        const emails = response.data;
        setEmailList(emails);
        setHasFetched(true);
      } else {
        throw new Error('Failed to fetch user list');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Error fetching users. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (showRowSecurity) {
      setSelectedEmail(
        chartControls.properties[propKey]?.rowSecurity?.filterPanels[currentOperator.toLowerCase()]
          ?.filters[0]?.userSelection[0] || '',
      );
    } else {
      setSelectedEmail(
        chartControls.properties[propKey]?.columnSecurity?.filterPanels[
          currentOperator.toLowerCase()
        ]?.filters[0]?.userSelection[0] || '',
      );
    }
  }, [
    chartControls.properties[propKey]?.columnSecurity,
    chartControls.properties[propKey]?.rowSecurity,
  ]);

  const handleToggleDropdown = () => {
    const newOpenState = !isOpen;
    setIsOpen((prev) => !prev);

    if (newOpenState && !hasFetched) {
      fetchUsers();
    }
  };

  return (
    <div
      style={{
        marginBottom: '20px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
          padding: '10px 5px',
          marginTop: '0px',
        }}
      >
        <Typography
          className="optionDescription"
          onClick={handleToggleDropdown}
          style={{
            marginTop: '0px',
            paddingTop: '0.25rem',
            paddingBottom: '0',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Impersonation{' '}
          <Tooltip
            title="Impersonation"
            slotProps={{
              tooltip: {
                sx: {
                  fontSize: '12px',
                },
              },
            }}
            placement="top"
            arrow
          >
            <InfoOutlineIcon
              fontSize="small"
              style={{
                marginLeft: '4px',
                verticalAlign: 'middle',
                color: '#888',
              }}
            />
          </Tooltip>
        </Typography>
        <IconButton
          size="small"
          onClick={() => {
            // Reset Impersonation
            dispatch(updateUserSelectionColumn(propKey, 'user', []));
            dispatch(updateUserSelection(propKey, 'user', []));
            // dispatch(updateUserSelection(propKey, 'group', []));
            // dispatch(updateUserSelectionColumn(propKey, 'group', []));
          }}
          sx={{
            padding: '5px 2px 2px 2px',
            marginLeft: '5px',
            color: 'lightgray',
            '&:hover': {
              color: 'gray',
              backgroundColor: 'transparent',
            },
          }}
          title="Reset Impersonation"
        >
          <RestartAltIcon style={{ fontSize: 16, color: 'gray' }} />
        </IconButton>
        <IconButton size="small" onClick={handleToggleDropdown}>
          {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={isOpen}>
        <div style={{ paddingLeft: '10px', marginTop: '5px' }}>
          {isLoading ? (
            <CircularProgress size={24} />
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <Box sx={{ textAlign: 'left' }}>
              <Typography
                variant="h6"
                style={{ fontWeight: 'bold', paddingLeft: '4px', paddingBottom: '8px' }}
                gutterBottom
              >
                Select a person to impersonate:
              </Typography>
              <Autocomplete
                options={emailList}
                value={selectedEmail}
                disabled={!showRowSecurity && !showColumnSecurity}
                onChange={(_, newValue) => {
                  const newSelection: any = selectedEmail === newValue ? [] : [newValue];
                  console.log('Selected Email:', newSelection);
                  setSelectedEmail(selectedEmail === newValue ? null : newValue);
                  if (showRowSecurity && showColumnSecurity) {
                    dispatch(
                      updateUserSelection(propKey, currentOperator.toLowerCase(), newSelection),
                    );
                    dispatch(
                      updateUserSelectionColumn(
                        propKey,
                        currentOperator.toLowerCase(),
                        newSelection,
                      ),
                    );
                  } else if (showRowSecurity) {
                    dispatch(
                      updateUserSelection(propKey, currentOperator.toLowerCase(), newSelection),
                    );
                  } else if (showColumnSecurity) {
                    dispatch(
                      updateUserSelectionColumn(
                        propKey,
                        currentOperator.toLowerCase(),
                        newSelection,
                      ),
                    );
                  } else {
                    // Fallback: apply to both by default if neither flag is set
                    dispatch(
                      updateUserSelection(propKey, currentOperator.toLowerCase(), newSelection),
                    );
                    dispatch(
                      updateUserSelectionColumn(
                        propKey,
                        currentOperator.toLowerCase(),
                        newSelection,
                      ),
                    );
                  }
                  if (
                    chartControls?.properties[propKey]?.rowSecurity?.filterPanels[
                      currentOperator.toLowerCase()
                    ]?.filters?.length > 0 ||
                    chartControls?.properties[propKey]?.columnSecurity?.filterPanels[
                      currentOperator.toLowerCase()
                    ]?.filters?.length > 1
                  ) {
                    dispatch(callQueryAPI(propKey, true));
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select an email"
                    variant="outlined"
                    size="small" // Add this for smaller default sizing
                    sx={{
                      '& .MuiInputLabel-root': {
                        color: '#484848',
                        fontFamily:
                          'Roboto-Regular, Roboto-Medium, Roboto-Bold, Roboto-Light, Segoe UI, system-ui, -apple-system, BlinkMacSystemFont, Helvetica, Arial, sans-serif',
                        fontWeight: 400,
                        fontSize: fontSize.medium,
                        lineHeight: '1.4375em',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        zIndex: 1,
                        pointerEvents: 'none',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 'calc(100% - 24px)',
                      },
                      '& .MuiOutlinedInput-root': {
                        paddingLeft: '8px',
                        height: '30px',
                        minHeight: '28px',
                        '& input': {
                          padding: '4px 8px !important',
                          height: 'auto',
                        },
                        '& .MuiAutocomplete-input': {
                          padding: '4px 8px !important',
                        },
                        '& .MuiAutocomplete-endAdornment': {
                          top: '50%',
                          transform: 'translateY(-50%)',
                        },
                      },
                      '& .MuiInputLabel-outlined': {
                        transform: 'translate(14px, 8px) scale(1)',
                        '&.MuiInputLabel-shrink': {
                          transform: 'translate(14px, -6px) scale(0.75)',
                        },
                      },
                    }}
                  />
                )}
                sx={{
                  width: '95%',
                  maxWidth: 400,
                  '& .MuiAutocomplete-inputRoot': {
                    paddingTop: '0px !important',
                    paddingBottom: '0px !important',
                  },
                }}
                disableClearable={!selectedEmail}
                clearOnBlur
              />
            </Box>
          )}
        </div>
      </Collapse>
    </div>
  );
};

export default Impersonation;
